/**
 * Permission Provider Wrapper for Consumer Portal
 *
 * Integrates the design-system PermissionProvider with the portal's
 * authentication context for permission-aware UI components.
 *
 * Note: Consumer Portal users (end consumers, referral partners) have
 * limited permission requirements compared to business/admin portals.
 * They primarily access their own data without granular permission checks.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 */

import { PermissionProvider, RequestAccessModalProvider, UpgradeModalProvider } from '@ops-matrix/design-system-ui';
import { SubscriptionTier } from '@ops-matrix/shared-types';
import type { ReactNode } from 'react';
import { useConsumerAuth } from './ConsumerAuthContext';

interface PermissionProviderWrapperProps {
  children: ReactNode;
}

/**
 * Wraps the application with permission-aware providers.
 *
 * Consumer Portal has simplified permission needs:
 * - END_CONSUMER: Access to their own cases, documents, proposals
 * - REFERRAL_PARTNER: Access to referral features and commissions
 *
 * Most features are tier-gated via the business's subscription,
 * not individual consumer permissions.
 */
export function PermissionProviderWrapper({ children }: PermissionProviderWrapperProps) {
  const { consumer, isAuthenticated, isLoading } = useConsumerAuth();

  // Consumer portal doesn't have individual permissions - features are controlled
  // by the business's subscription tier and consumer type
  const permissions: string[] = [];

  // Consumer's tier is determined by the business they belong to
  // Default to STARTER for authenticated consumers (businesses control actual tier)
  const currentTier = isAuthenticated ? SubscriptionTier.STARTER : SubscriptionTier.FREE;

  // Consumers don't have individual licenses - licenses are at business level
  const userLicenses: { type: string; status: 'active' | 'expired' | 'pending' | 'inactive'; expiresAt?: string }[] =
    [];

  // Handler for tier lock clicks - redirect to contact business
  const handleTierLockClick = (requiredTier: SubscriptionTier) => {
    console.log('[Consumer Permission] Feature requires higher tier:', requiredTier);
    // Could show a modal explaining the feature requires business upgrade
  };

  // Handler for permission lock clicks - consumers don't typically see these
  const handlePermissionLockClick = (requiredPermission: string) => {
    console.log('[Consumer Permission] Permission lock clicked:', requiredPermission);
  };

  // Handler for license lock clicks - not applicable to consumers
  const handleLicenseLockClick = (requiredLicense: string) => {
    console.log('[Consumer Permission] License lock clicked:', requiredLicense);
  };

  return (
    <PermissionProvider
      permissions={permissions}
      role={consumer?.consumerType ?? 'end_consumer'}
      tier={currentTier}
      userLicenses={userLicenses}
      isLoading={isLoading}
      onTierLockClick={handleTierLockClick}
      onPermissionLockClick={handlePermissionLockClick}
      onLicenseLockClick={handleLicenseLockClick}
    >
      <UpgradeModalProvider currentTier={currentTier}>
        <RequestAccessModalProvider
          onRequestAccess={async (request) => {
            // Consumer portal users typically don't request access
            console.log('[Consumer RequestAccess] Submitted request:', request);
          }}
          adminEmail="support@opsmatrix.com"
        >
          {children}
        </RequestAccessModalProvider>
      </UpgradeModalProvider>
    </PermissionProvider>
  );
}

export default PermissionProviderWrapper;
